/**
 * Decodes a JWT's payload segment without verifying its signature.
 * @param token - encoded JWT
 * @returns the payload claims, or undefined if the token is unreadable
 */
function decodeJwtPayload(token: string): Record<string, unknown> | undefined {
  const segments = token.split(".");
  if (segments.length !== 3) return undefined;

  try {
    const base64 = segments[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "=",
    );
    // atob yields bytes, not characters — percent-escaping them first keeps a
    // non-ASCII claim (an accented email) from corrupting the JSON.
    const json = decodeURIComponent(
      Array.from(
        atob(padded),
        (c) => `%${c.charCodeAt(0).toString(16).padStart(2, "0")}`,
      ).join(""),
    );
    const parsed: unknown = JSON.parse(json);
    return typeof parsed === "object" && parsed !== null
      ? (parsed as Record<string, unknown>)
      : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Reports whether a JWT's `exp` claim has already passed.
 *
 * The signature is not checked — the server stays the authority on validity;
 * this only avoids sending a token already known to be dead.
 *
 * @param token - encoded JWT
 * @param skewMs - treat the token as expired this many ms early (default 30s)
 * @returns true only when a numeric `exp` is readable and in the past
 */
export function isJwtExpired(token: string, skewMs = 30_000): boolean {
  const exp = decodeJwtPayload(token)?.exp;
  // Unreadable or exp-less tokens are left alone: the backend's LooseValidAt
  // accepts a signed token without exp, so evicting would re-auth every visit.
  if (typeof exp !== "number" || !Number.isFinite(exp)) return false;
  return exp * 1000 <= Date.now() + skewMs;
}
